import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCheck, faPen, faTriangleExclamation, faXmark } from "@fortawesome/pro-regular-svg-icons";

import trashIcon from "@/assets/icons/trashIcon.svg?url";

import { Icon, LoadingOverlay, Modal, PermissionCheck } from "@/components";

import { useTableSearch } from "@/utils";

import { PermissionRoles } from "@/enum";

import {
	useDeleteWebhookMutation,
	useGetWebhooksQuery,
} from "@/pages/Private/redux/webhook/webhook.api";

import { Table, TableColumn } from "@/components/Table/Table";

import { ButtonColor } from "@/components/Button/types";

import { Webhook } from "../schema/webhook";
import { UpdateWebhookModal } from "./UpdateWebhookModal";
import { WebhookHealthCell } from "./WebhookHealthCell";
import { WebhookListsCell } from "./WebhookListsCell";
import { WebhookRowMenu } from "./signing/WebhookRowMenu";
import { WebhookSigningCell } from "./signing/WebhookSigningCell";
import { WebhookSigningActions } from "./signing/useWebhookSigningDialogs";

interface WebhookTableProps {
	/** Signing actions (PREC-4249): the row menu and the Edit dialog's Signing section. */
	signingActions?: WebhookSigningActions;
}

export const WebhookTable: FunctionComponent<WebhookTableProps> = ({ signingActions }) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`webhook.${key}`), [t]);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
	const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
	const { page, setPage, limit, setLimit } = useTableSearch();
	const [deleteWebhook, { isLoading }] = useDeleteWebhookMutation();

	const { data, isFetching } = useGetWebhooksQuery({
		page,
		limit,
	});

	const handleDeleteModal = (row: Webhook) => {
		setShowDeleteModal(true);
		setSelectedWebhook(row);
	};

	const columns: TableColumn[] = useMemo(
		() => [
			{
				title: "Name",
				uid: "name",
				render: (row) => <div className="flex items-start justify-start">{row?.name}</div>,
			},
			{
				title: "Webhook URL",
				uid: "url",
				// Masked by the API where it carries a secret (PREC-4253); the stored URL never gets here.
				render: (row) => (
					<div
						className="flex items-start justify-start font-mono text-[13px] break-all"
						data-test="webhook-url"
					>
						{row?.url}
					</div>
				),
			},
			{
				title: "Receives Data",
				uid: "receivesData",
				render: (row) => (
					<div className="flex items-start justify-start">
						{row?.receivesData ? <Icon icon={faCheck} /> : <Icon icon={faXmark} />}
					</div>
				),
			},
			{
				title: t("webhookLists.column"),
				uid: "lists",
				render: (row) => <WebhookListsCell webhook={row} />,
			},
			{
				title: t("webhookHealth.column"),
				uid: "health",
				render: (row) => <WebhookHealthCell webhook={row} />,
			},
			{
				title: t("webhookSigning.column"),
				uid: "signing",
				render: (row) => <WebhookSigningCell webhook={row} />,
			},
			{
				title: "",
				uid: "actions",
				render: (row) => (
					<div className="flex items-center gap-2 justify-end">
						{signingActions && (
							<PermissionCheck requiredPermissions={[PermissionRoles.WEBHOOK]}>
								<WebhookRowMenu actions={signingActions} webhook={row} />
							</PermissionCheck>
						)}
						<PermissionCheck requiredPermissions={[PermissionRoles.WEBHOOK]}>
							<button
								className="cursor-pointer"
								onClick={() => {
									setSelectedWebhook(row);
									setShowUpdateModal(true);
								}}
							>
								<Icon className="text-text-meta w-4 h-4" icon={faPen} size="lg" />
							</button>
						</PermissionCheck>
						<PermissionCheck requiredPermissions={[PermissionRoles.WEBHOOK]}>
							<button className="cursor-pointer" onClick={() => handleDeleteModal(row)}>
								<img alt="trash" src={trashIcon} />
							</button>
						</PermissionCheck>
					</div>
				),
			},
		],
		[ts, handleDeleteModal, signingActions]
	);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	const handleDeleteWebhook = async () => {
		try {
			await deleteWebhook(selectedWebhook?.id ?? 0).unwrap();
			setShowDeleteModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	// The dialogs stay outside the loading branch: a refetch must not unmount an open Edit dialog and
	// throw away what the user typed (PREC-4253).
	return (
		<div className="w-full relative">
			{isFetching ? (
				<LoadingOverlay />
			) : (
				<div className="w-full bg-white">
					<div className="pr-4 pb-[20px]">
						<Table
							columns={columns}
							data={data?.data || []}
							handleChangeRowsPerPage={handleChangeRowsPerPage}
							handleFetchPage={(page) => {
								setPage(page);
							}}
							hidePagination={false}
							limit={limit}
							overallCount={data?.meta?.itemCount}
							page={page}
							sticky={false}
							totalPages={data?.meta?.pageCount}
						/>
					</div>
				</div>
			)}
			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={handleDeleteWebhook}
					isLoading={isLoading}
					isOpened={showDeleteModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={"Delete"}
					title={"Delete Webhook"}
				>
					<div className="flex flex-row gap-4 items-start p-2 justify-start text-sm mb-2 text-text border border-status-warning rounded-md">
						<Icon className="text-status-warning w-4 h-4" icon={faTriangleExclamation} />
						You will be unable to perform any more actions with this webhook.
					</div>
				</Modal>
			)}
			{showUpdateModal && selectedWebhook && (
				<UpdateWebhookModal
					isOpen={showUpdateModal}
					setIsOpen={setShowUpdateModal}
					signingActions={signingActions}
					webhook={selectedWebhook}
				/>
			)}
		</div>
	);
};
